import { createElement, type SVGAttributes } from 'react';

/** 16×16 outline icons aligned with Notion’s default database property icons. */
const iconProps: SVGAttributes<SVGSVGElement> = {
  xmlns: 'http://www.w3.org/2000/svg',
  viewBox: '0 0 16 16',
  fill: 'none',
  width: 16,
  height: 16,
  'aria-hidden': true,
  className: 'cs-db-col-type-icon',
};

const stroke = {
  stroke: 'currentColor',
  strokeWidth: 1.35,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

/** Notion’s title property uses the table/grid icon, not “Aa”. */
function ColumnTypeTitleIcon() {
  return createElement(
    'svg',
    iconProps,
    createElement('rect', { ...stroke, x: 2.75, y: 2.75, width: 10.5, height: 10.5, rx: 1 }),
    createElement('path', { ...stroke, d: 'M2.75 6h10.5' }),
    createElement('path', { ...stroke, d: 'M2.75 9.25h10.5' }),
    createElement('path', { ...stroke, d: 'M5.75 2.75v10.5' }),
  );
}

function ColumnTypeTextIcon() {
  return createElement(
    'svg',
    iconProps,
    createElement('path', { ...stroke, d: 'M3.5 5.25h9M3.5 8h6.5M3.5 10.75h4' }),
  );
}

function ColumnTypeNumberIcon() {
  return createElement(
    'svg',
    iconProps,
    createElement('path', { ...stroke, d: 'M6.25 3.25v9.5M9.75 3.25v9.5M4.75 6.75h6.5M4.75 9.25h6.5' }),
  );
}

function ColumnTypeDateIcon() {
  return createElement(
    'svg',
    iconProps,
    createElement('path', {
      ...stroke,
      d: 'M3.25 4.75h9.5a1 1 0 0 1 1 1v6.5a1 1 0 0 1-1 1H3.25a1 1 0 0 1-1-1v-6.5a1 1 0 0 1 1-1Z',
    }),
    createElement('path', { ...stroke, d: 'M2.25 7.25h11.5M5.5 3.25v2M10.5 3.25v2' }),
  );
}

function ColumnTypeSelectIcon() {
  return createElement(
    'svg',
    iconProps,
    createElement('path', { ...stroke, d: 'M3.5 5.25h6M3.5 8h6M3.5 10.75h4.5' }),
    createElement('path', { ...stroke, d: 'M11.25 6.75v4.5M10 10.75l1.25 1.25 1.25-1.25' }),
  );
}

function ColumnTypeMultiSelectIcon() {
  return createElement(
    'svg',
    iconProps,
    createElement('circle', {
      fill: 'currentColor',
      stroke: 'none',
      cx: 3.35,
      cy: 5.25,
      r: 0.85,
    }),
    createElement('circle', {
      fill: 'currentColor',
      stroke: 'none',
      cx: 3.35,
      cy: 8,
      r: 0.85,
    }),
    createElement('circle', {
      fill: 'currentColor',
      stroke: 'none',
      cx: 3.35,
      cy: 10.75,
      r: 0.85,
    }),
    createElement('path', { ...stroke, d: 'M5.25 5.25h7.25M5.25 8h7.25M5.25 10.75h7.25' }),
  );
}

function ColumnTypeCheckboxIcon() {
  return createElement(
    'svg',
    iconProps,
    createElement('path', {
      ...stroke,
      d: 'M4.25 4.25h7.5a1.25 1.25 0 0 1 1.25 1.25v5.5a1.25 1.25 0 0 1-1.25 1.25h-7.5a1.25 1.25 0 0 1-1.25-1.25v-5.5a1.25 1.25 0 0 1 1.25-1.25Z',
    }),
  );
}

function ColumnTypeRelationIcon() {
  return createElement(
    'svg',
    iconProps,
    createElement('path', {
      ...stroke,
      d: 'M3.25 5.25h3.25v5.5H3.25a.75.75 0 0 1-.75-.75v-4a.75.75 0 0 1 .75-.75Z',
    }),
    createElement('path', {
      ...stroke,
      d: 'M9.5 5.25h3.25a.75.75 0 0 1 .75.75v4a.75.75 0 0 1-.75.75H9.5v-5.5Z',
    }),
    createElement('path', { ...stroke, d: 'M7.25 8h1.5M8 8l-.75.75M8 8l.75.75' }),
  );
}

function ColumnTypeUrlIcon() {
  return createElement(
    'svg',
    iconProps,
    createElement('path', {
      ...stroke,
      d: 'M6.1 9.9a2.25 2.25 0 0 0 3.18 0l1.42-1.42a2.25 2.25 0 0 0-3.18-3.18L7.1 6.1',
    }),
    createElement('path', {
      ...stroke,
      d: 'M9.9 6.1a2.25 2.25 0 0 0-3.18 0L5.3 7.52a2.25 2.25 0 0 0 3.18 3.18l1.42-1.42',
    }),
  );
}

function ColumnTypeRollupIcon() {
  return createElement(
    'svg',
    iconProps,
    createElement('path', {
      ...stroke,
      d: 'M5.5 12.25h5M8 9.75v5M11.5 7.5v7.5',
    }),
    createElement('path', {
      ...stroke,
      d: 'M4.75 5.25h6.5a.75.75 0 0 1 .75.75v8.5a.75.75 0 0 1-.75.75h-6.5a.75.75 0 0 1-.75-.75V6a.75.75 0 0 1 .75-.75Z',
    }),
  );
}

const COLUMN_TYPE_ICON_BY_TYPE: Record<string, () => ReturnType<typeof createElement>> = {
  text: ColumnTypeTextIcon,
  rich_text: ColumnTypeTextIcon,
  title: ColumnTypeTitleIcon,
  number: ColumnTypeNumberIcon,
  date: ColumnTypeDateIcon,
  select: ColumnTypeSelectIcon,
  multi_select: ColumnTypeMultiSelectIcon,
  checkbox: ColumnTypeCheckboxIcon,
  relation: ColumnTypeRelationIcon,
  rollup: ColumnTypeRollupIcon,
  url: ColumnTypeUrlIcon,
};

export function ColumnTypeIcon({ type }: { type: string }) {
  const Icon = COLUMN_TYPE_ICON_BY_TYPE[type] ?? ColumnTypeTextIcon;
  return Icon();
}
